import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BrandM,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { FOOTBALL_PLAY_EVENTTYPE } from '@sorare/core/src/constants/__generated__/routes';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import limited from './assets/limited.webp';
import rare from './assets/rare.webp';
import superRare from './assets/superRare.webp';
import unique from './assets/unique.webp';

const Root = styled(Vertical)`
  height: 100%;
  gap: var(--quadruple-unit);
  padding: 0 var(--double-unit);
`;
const Footer = styled(Horizontal).attrs({ center: true })`
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  }
`;
const ScarcityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  flex: 1;
  gap: var(--double-unit);
  min-height: 0;

  @media ${tabletAndAbove} {
    min-height: 400px;
  }
`;
const Cell = styled(Vertical)`
  min-height: 0;
  min-width: 0;
`;
const ImgWrapper = styled(Horizontal).attrs({ center: true })`
  aspect-ratio: 1;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  background: var(--background) border-box;
  max-width: 100%;
  min-height: 0;
  height: 100%;
`;
const Img = styled.img`
  height: 80%;
`;

const scarcities = [
  {
    name: 'Limited',
    nb: 1000,
    img: limited,
    background: 'linear-gradient(180deg, #000 59.65%, #302000 100%)',
  },
  {
    name: 'Rare',
    nb: 100,
    img: rare,
    background: 'linear-gradient(180deg, #000 59.65%, #540000 100%)',
  },
  {
    name: 'Super Rare',
    nb: 10,
    img: superRare,
    background: 'linear-gradient(180deg, #000 59.65%, #002C54 100%)',
  },
  {
    name: 'Unique',
    nb: 1,
    img: unique,
    background: 'linear-gradient(180deg, #000 59.65%, #340054 100%)',
  },
];

type Props = { open: boolean; onClose: () => void };
export const NewNavDialog = ({ open, onClose }: Props) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      footer={
        <Footer>
          <Button
            size="large"
            color="tertiary"
            to={generatePath(FOOTBALL_PLAY_EVENTTYPE, {
              eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
            })}
            fullWidth
          >
            <FormattedMessage
              id="NewNavDialog.viewCompetitions"
              defaultMessage="View Competitions"
            />
          </Button>
          <Button size="large" color="secondary" onClick={onClose} fullWidth>
            <FormattedMessage
              id="NewNavDialog.viewMarket"
              defaultMessage="View Market"
            />
          </Button>
        </Footer>
      }
    >
      <Root className="text-center">
        <Vertical>
          <BrandM as="h2">
            <FormattedMessage
              id="NewNavDialog.title"
              defaultMessage="New Navigation"
            />
          </BrandM>
          <BodyM as="p">
            <FormattedMessage
              id="NewNavDialog.subtitle"
              defaultMessage="You can view all the new unlocked Pro requirements per scarcity in each competition page."
            />
          </BodyM>
        </Vertical>
        <ScarcityGrid>
          {scarcities.map(({ name, nb, img, background }) => (
            <Cell key={name} gap={1.5} center>
              <ImgWrapper
                style={{ '--background': background } as CSSProperties}
              >
                <Img src={img} alt="" />
              </ImgWrapper>
              <Vertical gap={0} center>
                <LabelL>{name}</LabelL>
                <LabelM color="var(--c-nd-600)">
                  <FormattedMessage
                    id="NewNavDialog.numberOfCards"
                    defaultMessage="{nb, plural, one {{nb} Player Card} other {{nb} Player Cards}}"
                    values={{ nb }}
                  />
                </LabelM>
              </Vertical>
            </Cell>
          ))}
        </ScarcityGrid>
      </Root>
    </Dialog>
  );
};
